import { useContext, useMemo } from 'react'

import {
  ArcadeModalHeader,
  ArcadeModalPortalProps,
  IsInArcadeModalContext,
  useArcadeModalActionOverride,
} from '@cash-design-system/react'
import { useModalSharedButtonProps } from '@cash-web/shared-feature-modal'

export interface BlockerActionHeaderProps {
  onBack?: () => void
  onClose?: () => void
}

/**
 * The actual rendering of the action header is handled by the modal.
 */
const ModalBlockerActionHeader = ({
  backButtonProps,
  closeButtonProps,
}: Pick<ArcadeModalPortalProps, 'closeButtonProps' | 'backButtonProps'>) => {
  useArcadeModalActionOverride(
    useMemo(
      () => ({
        type: 'buttons' as const,
        ...(backButtonProps?.onBack ? { backButtonProps } : {}),
        ...(closeButtonProps?.onClose ? { closeButtonProps } : {}),
      }),
      [backButtonProps, closeButtonProps]
    )
  )
  return null
}

/**
 * Handles the case when a blocker wants to override the behavior of the action header.
 * If the blocker is in a modal, we'll use the useArcadeModalActionOverride hook to override the action header.
 * If the blocker is not in a modal, it will return the default action header that should be rendered by the blocker.
 * @param onBack - The function to call when the back button is clicked.
 * @param onClose - The function to call when the close button is clicked.
 */
export const BlockerActionHeader = ({ onBack, onClose }: BlockerActionHeaderProps) => {
  const isInArcadeModal = useContext(IsInArcadeModalContext)

  const sharedModalButtonProps = useModalSharedButtonProps({
    closeButtonProps: { onClose },
    backButtonProps: { onBack },
  })

  if (!onBack && !onClose) {
    return null
  }

  if (isInArcadeModal) {
    return <ModalBlockerActionHeader {...sharedModalButtonProps} />
  }

  return <ArcadeModalHeader {...sharedModalButtonProps} />
}
